import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { ScrollView } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { ANSWERS_CARD_CLASS } from '../answers/answersLayout';

import { CommentsSkeleton } from '@/components/CommentsSkeleton';
import { Box } from '@/components/ui/box';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Skeleton } from '@/components/ui/skeleton';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/**
 * Loading placeholders for the Inquiry Library, drawn to the geometry of the
 * real list card and post so nothing moves when the content lands.
 *
 * Structurally empty and fixed-size on purpose: a placeholder that wraps real
 * content in a hidden style flashes on native, because content and style cross
 * the bridge in separate frames.
 *
 * Each placeholder reads to a screen reader as one loading indicator, with the
 * bars inside hidden, rather than as a run of unlabelled shapes.
 */

/** Paragraph shapes for a post body: a speaker label, then its lines. */
const POST_BODY_PARAGRAPHS = [
  { label: 'w-24', lines: ['w-full', 'w-full', 'w-full', 'w-5/6'] },
  { label: 'w-24', lines: ['w-full', 'w-1/2'] },
  { label: 'w-36', lines: ['w-full', 'w-full', 'w-full', 'w-2/3'] },
];

/** A signed-out reader's post is a short preview: the opening label and a line or two. */
const PREVIEW_BODY_PARAGRAPHS = [{ label: 'w-24', lines: ['w-full', 'w-1/3'] }];

/**
 * Placeholder shown while the library list is fetching.
 *
 * Carries `library-loading` so a test can tell "still loading" apart from
 * "loaded and empty", which otherwise look identical from the outside.
 *
 * `framed` draws the web page's card: the same white `outline-200` box, radius and 16px padding
 * as a real post, 16px apart with no divider, so the swap to posts moves nothing. Native keeps
 * its flat rows with a divider.
 */
export function PostCardSkeleton({
  count = 4,
  framed = false,
}: {
  count?: number;
  framed?: boolean;
}) {
  return (
    <VStack
      testID="library-loading"
      className={framed ? 'gap-4' : undefined}
      {...getAccessibilityProps({ label: 'Loading posts', role: 'progressbar' })}>
      {[...Array(count)].map((_, index) => (
        <Box key={index} aria-hidden testID="library-loading-card">
          <VStack className={twJoin('gap-4 p-4', framed && ANSWERS_CARD_CLASS)}>
            <HStack className="items-center justify-between">
              <Skeleton className="h-6 w-24" variant="rounded" />

              <Skeleton className="h-4 w-20" />
            </HStack>

            <VStack>
              <VStack className="mb-2 gap-2">
                <Skeleton className="h-6 w-full" />

                <Skeleton className="h-6 w-2/3" />
              </VStack>

              <VStack className="gap-2">
                <Skeleton className="h-4 w-full" />

                <Skeleton className="h-4 w-full" />

                <Skeleton className="h-4 w-11/12" />
              </VStack>
            </VStack>

            <HStack className="items-center justify-between">
              <HStack className="items-center gap-5">
                <Skeleton className="h-5 w-10" variant="rounded" />

                <Skeleton className="h-5 w-10" variant="rounded" />
              </HStack>

              <HStack className="items-center gap-5">
                <Skeleton className="h-5 w-5" variant="rounded" />

                <Skeleton className="h-5 w-5" variant="rounded" />
              </HStack>
            </HStack>
          </VStack>

          {framed ? null : <Divider />}
        </Box>
      ))}
    </VStack>
  );
}

type PostDetailSkeletonProps = {
  /** Whether the post carries an inquiry summary the reader is shown. */
  hasSummary?: boolean;
  /**
   * How many comments the post has, when the list already said. `0` draws the
   * empty state's footprint; `undefined` (a deep link) draws no comment rows,
   * since a guess would jump either way.
   */
  commentCount?: number;
  /** The reader only gets a preview of the body, so draw a short one. */
  isPreview?: boolean;
};

/**
 * Placeholder shown while a single post is fetching: the whole post, not just
 * its top, so the page keeps its shape when the real content lands.
 *
 * The summary card and the comment rows follow what the list knew about this
 * post, which is what stops them popping in or vanishing on load.
 */
export function PostDetailSkeleton({
  hasSummary = false,
  commentCount,
  isPreview = false,
}: PostDetailSkeletonProps) {
  const paragraphs = isPreview ? PREVIEW_BODY_PARAGRAPHS : POST_BODY_PARAGRAPHS;

  return (
    <VStack
      testID="library-post-loading"
      className="flex-1"
      {...getAccessibilityProps({ label: 'Loading post', role: 'progressbar' })}>
      {/* A scroll view like the real post's, so the page's height cannot squeeze
          the placeholder's sections into each other. */}
      <ScrollView
        aria-hidden
        scrollEnabled={false}
        className="flex-1"
        contentContainerClassName="flex min-h-full">
        <VStack
          className={twJoin(
            'gap-4 border-b border-b-outline-100 pb-8 pt-6',
            // The web reading column the loaded post uses, so nothing shifts when it lands.
            isWeb ? 'mx-auto w-full max-w-3xl px-4 md:px-6' : 'px-5'
          )}>
          <HStack className="items-center justify-between pt-4 md:pt-0">
            <Skeleton className="h-6 w-20" variant="rounded" />

            <Skeleton className="h-4 w-20" />
          </HStack>

          <VStack className="gap-2">
            <Skeleton className="h-7 w-full" />

            {/* Titles usually run to a second line on a phone and fit on one at desktop width. */}
            <Skeleton className="h-7 w-3/5 md:hidden" />
          </VStack>

          {hasSummary ? <SummaryCardSkeleton /> : null}

          <VStack className="gap-5">
            {paragraphs.map((paragraph, paragraphIndex) => (
              <VStack key={paragraphIndex} className="gap-2">
                <Skeleton className={twJoin('h-4', paragraph.label)} />

                {paragraph.lines.map((width, lineIndex) => (
                  <Skeleton key={lineIndex} className={twJoin('h-4', width)} />
                ))}
              </VStack>
            ))}
          </VStack>
        </VStack>

        <CommentsSkeleton commentCount={commentCount} />
      </ScrollView>
    </VStack>
  );
}

/**
 * The collapsed inquiry summary, part for part: the badge row, the question and
 * conclusion, the case line, the fold control, then the footer with the
 * provenance note and the thumbs. Its ground matches the card's.
 */
function SummaryCardSkeleton() {
  return (
    <VStack
      testID="library-post-loading-summary"
      className="w-full gap-3 rounded-md border border-outline-500 bg-background-50 p-3 md:p-4">
      <HStack className="flex-wrap items-center gap-2">
        <Skeleton className="h-5 w-36" variant="rounded" />

        <Skeleton className="h-4 w-28" />
      </HStack>

      <VStack className="gap-2">
        <Skeleton className="h-4 w-full" />

        <Skeleton className="h-4 w-4/5" />
      </VStack>

      <Skeleton className="h-11 w-full" variant="rounded" />

      <Skeleton className="h-4 w-20" />

      <HStack className="items-center justify-between gap-3 pt-1">
        <Skeleton className="h-4 w-36" />

        <Skeleton className="h-6 w-28" variant="rounded" />
      </HStack>
    </VStack>
  );
}
